import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// import cn from "classnames";
import { ButtonType } from "@components/new/Button";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import Checkbox from "@components/new/Checkbox";
import SelectDropdown, { DropdownItem } from "@components/SelectDropdown";
// import { Title3 } from "@components/new/Typo";
import { TSellerWarehouse, TWarehouse } from "@api/apiClient/fbp";
import ConfirmModal from "@components/modals/ConfirmModal";
import LinkModal from "../modals/LinkModal";
// import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { getWarehouseSelectValue, getWarehouseSelectOption } from "./WarehouseSelectComponents";
import IconLink from '@assets/icons/outline/git-compare.svg?react';
import styles from './WarehouseSelectComponents.module.scss';

export const WarehouseSelect = ({ onChange, value, items, activeWarehouse, editWarehouse }: {
  onChange: (value: string) => void;
  value?: string;
  items: TSellerWarehouse[];
  activeWarehouse?: TWarehouse;
  editWarehouse: (item: TSellerWarehouse | null) => void;
}) => {
  const { t } = useTranslation();
  const [isOpenConfirmWindow, setOpenConfirmWindow] = useState<boolean>(false);
  const [isLinkWindowOpen, setLinkWindowOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<string>>(
    new Set<string>(),
  );

  const handleToggleSelectAll = useCallback(() => {
    setSelected((currentSelected: Set<string>) => {
      return currentSelected.size ? new Set()
        : new Set(items.filter(v => (!v.isAwaitingDeletion)).map(v => v.id));
    });
  }, [items]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelected((currentSelected: Set<string>) => {
      const newSelected = new Set(currentSelected);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  }, []);

  const WarehouseSelectValue = useMemo(() => getWarehouseSelectValue({
    editWarehouse,
    yAvailable: Boolean(activeWarehouse?.fbpAvailableMarketplaces?.yandex),
    wbAvailable: Boolean(activeWarehouse?.fbpAvailableMarketplaces?.wildberries),
  }), [activeWarehouse]);

  const WarehouseSelectItem = useMemo(() => getWarehouseSelectOption({
    editWarehouse,
    yAvailable: Boolean(activeWarehouse?.fbpAvailableMarketplaces?.yandex),
    wbAvailable: Boolean(activeWarehouse?.fbpAvailableMarketplaces?.wildberries),
    aliAvailable: Boolean(activeWarehouse?.fbpAvailableMarketplaces?.aliexpress),
    onCheck: handleToggleSelect,
    isChecked: (id: string) => selected.has(id),
  }), [activeWarehouse, selected]);

  return (
    <>
      <SelectDropdown<TSellerWarehouse>
        value={value}
        onItemSelect={onChange}
        items={items}
        keyExtractor={(item: TSellerWarehouse) => item.id}
        SelectedItemComponent={WarehouseSelectValue}
        ItemComponent={WarehouseSelectItem}
        // hideChevron={sellerWarehouses.length === 1}
        inputWidth="300px"
        dropdownWidth="300px"
        dropdownAlign="start"
        header={(
          <DropdownItem>
            <span className={styles.selectAll}>
              <Checkbox
                checked={!selected.size ? false : ((selected.size < items.length) ? "partial" : true)}
                onChange={handleToggleSelectAll}
              />
              {t('fbpPage.storesUnion.selectAll')}
            </span>
          </DropdownItem>
        )}
        footer={(
          <DropdownItem>
            <LinkButton
              icon={IconLink}
              size={LinkButtonSize.M}
              title={t('fbpPage.storesUnion.linkBtn')}
              onClick={() => setOpenConfirmWindow(true)}
              disabled={selected.size < 2}
            />
          </DropdownItem>
        )}
      />

      {isOpenConfirmWindow && (
        <ConfirmModal
          title={t("fbpPage.storesUnion.linkModalTitle")}
          description={t("fbpPage.storesUnion.linkModalDescription")}
          noBtnTitle={t("fbpPage.storesUnion.cancelBtn")}
          yesBtnTitle={t("fbpPage.storesUnion.connectBtn")}
          onClose={() => setOpenConfirmWindow(false)}
          requestFn={() => setLinkWindowOpen(true)}
          onSuccess={() => {}}
          type="info"
          inModal
          yesBtnType={ButtonType.PRIMARY}
        />
      )}

      {isLinkWindowOpen && (
        <LinkModal
          onClose={() => setLinkWindowOpen(false)}
          sellerWarehouseIds={Array.from(selected)}
        />
      )}
    </>
  );
}
